import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Search, X, TrendingUp, Package } from 'lucide-react';
import { ProductCard } from '../components/custom/ProductCard';
import { motion } from 'framer-motion';

function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchPerformed, setSearchPerformed] = useState(false);

  const popularSearches = [
    'School Bag',
    'Lunch Box',
    'Water Bottle',
    'Toys',
    'Stationery',
    'Books'
  ];

  useEffect(() => {
    const searchQuery = searchParams.get('q');
    if (searchQuery) {
      setQuery(searchQuery);
      performSearch(searchQuery);
    }
  }, [searchParams]);

  const performSearch = async (searchTerm) => {
    if (!searchTerm.trim()) {
      setResults([]);
      setSearchPerformed(false);
      return;
    }

    setLoading(true);
    setSearchPerformed(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/products/search?q=${encodeURIComponent(searchTerm)}`
      );
      const data = await response.json();
      setResults(data.products || []);
    } catch (error) {
      console.error('Search failed:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (query.trim()) {
      setSearchParams({ q: query });
      performSearch(query);
    }
  };

  const handlePopularSearch = (term) => {
    setQuery(term);
    setSearchParams({ q: term });
    performSearch(term);
  };

  const clearSearch = () => {
    setQuery('');
    setResults([]);
    setSearchPerformed(false);
    setSearchParams({});
  };

  return (
    <div className="min-h-screen py-6 px-4 pb-20 md:pb-8 bg-gray-50">
      <Helmet>
        <title>{query ? `Search: ${query} - Mothering` : 'Search Products - Mothering'}</title>
        <meta name="description" content="Search for kids products, toys, school supplies, and more" />
      </Helmet>

      <div className="container mx-auto max-w-6xl">
        {/* Search Bar */}
        <Card className="p-4 mb-6">
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search for products, categories, age groups..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="pl-10 pr-10 h-12 text-base"
                autoFocus
                data-testid="search-input"
              />
              {query && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>
            <Button type="submit" size="lg" className="px-8" data-testid="search-button">
              Search
            </Button>
          </form>
        </Card>

        {/* Popular Searches */}
        {!searchPerformed && (
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-5 h-5 text-cyan-600" />
              <h2 className="text-lg font-semibold">Popular Searches</h2>
            </div>
            <div className="flex flex-wrap gap-2">
              {popularSearches.map((term) => (
                <Badge
                  key={term}
                  variant="outline"
                  className="cursor-pointer hover:bg-cyan-50 hover:border-cyan-300 transition-colors px-4 py-2 text-sm"
                  onClick={() => handlePopularSearch(term)}
                >
                  {term}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Search Results */}
        {searchPerformed && (
          <>
            <div className="mb-4">
              <h2 className="text-xl font-bold">
                {loading ? 'Searching...' : `Search Results for "${query}"`}
              </h2>
              {!loading && (
                <p className="text-muted-foreground">
                  {results.length} {results.length === 1 ? 'product' : 'products'} found
                </p>
              )}
            </div>

            {loading ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="h-80 bg-white rounded-xl animate-pulse" />
                ))}
              </div>
            ) : results.length === 0 ? (
              <div className="text-center py-16">
                <Package className="w-20 h-20 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-xl font-semibold mb-2">No products found</h3>
                <p className="text-muted-foreground mb-6">
                  Try searching with different keywords or browse our categories
                </p>
                <Button onClick={() => navigate('/products')}>
                  Browse All Products
                </Button>
              </div>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"
              >
                {results.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </motion.div>
            )}
          </>
        )}

        {/* Quick Links when no search */}
        {!searchPerformed && (
          <div className="mt-8">
            <h3 className="text-lg font-semibold mb-4">Browse by Category</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { name: 'Education', icon: '📚' },
                { name: 'Toys', icon: '🧸' },
                { name: 'School Bags', icon: '🎒' },
                { name: 'Lunch Boxes', icon: '🍱' }
              ].map((cat) => (
                <Card
                  key={cat.name}
                  className="p-6 text-center cursor-pointer hover:shadow-md hover:border-cyan-300 transition-all"
                  onClick={() => navigate(`/products?category=${cat.name.toLowerCase()}`)}
                >
                  <div className="text-4xl mb-2">{cat.icon}</div>
                  <p className="font-medium">{cat.name}</p>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SearchPage;
